// src/components/mitchRoom/modelOutputs/ExplanationModal.js
//
// Renders /getPredictionExplanation for one (entity, fixture). Two kinds of
// row arrive (decided server-side by model_name prefix — see
// services/api/services/explanation_service.py):
//
//   explanation_kind === 'components' (v2, Sep 2026)
//     components: {name: value} additive contributions.
//       player (link 'identity'): points, Σ components === prediction, baseline 0.
//       team   (link 'log'):      log-λ terms, exp(baseline + Σ) === prediction.
//     features: player → {prob_starting, prob_playing, prob_60_start, lambda_own,
//       lambda_opp, lambda_own_exp, lambda_opp_exp, home, n_starts, prior_only,
//       rates{…}, e_start_components{…}}; team → {raw:{elo_diff, home, xg_for_own,
//       xg_against_opp}, std:{same}, elo_own, elo_opp}.
//     e_start_counts / e_start_probs / p_play (optional, v2 player): per-start
//       expected counts and probabilities behind each component ("Expected" column).
//   explanation_kind === 'shap' (legacy rows, or an older API without the field)
//     shap_values: {feature: shap}, features: {feature: value}.
//
// Every field is optional — sample: services/api/logs/response_samples/_api_getPredictionExplanation*.json
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';
import { getPredictionExplanation } from '../../../services/api';

// Known abbreviations that generic title-casing would mangle (xg -> Xg).
const FEATURE_ABBREVIATIONS = {
  xg: 'xG',
  xa: 'xA',
  npxg: 'npxG',
  cs: 'CS',
  elo: 'Elo',
  gw: 'GW',
  h2h: 'H2H',
  fpl: 'FPL',
  mins: 'Mins',
  avg: 'Avg',
  yc: 'YC',
  rc: 'RC',
  og: 'OG',
  pen: 'Pen',
  opp: 'Opp',
  std: 'Std',
  ewma10: 'EWMA10',
};

// Player component labels (spec key list). Anything else falls back to
// formatFeatureName so an extra/renamed key still renders.
const PLAYER_COMPONENT_LABELS = {
  appearance: 'Appearance',
  goals: 'Goals',
  assists: 'Assists',
  clean_sheet: 'Clean sheet',
  conceded: 'Goals conceded',
  defcon: 'Defensive contribution',
  saves: 'Saves',
  bonus: 'Bonus',
  cards: 'Cards',
  own_goals: 'Own goals',
  penalties: 'Penalties',
  cameo: 'Cameo (sub appearance)',
};

// Team log-λ term labels — best-effort; unknown keys fall back to formatting.
const TEAM_TERM_LABELS = {
  elo_diff: 'Elo difference (own − opp) / 100',
  home: 'Home advantage',
  xg_for_own: 'Own attack xG (ewma10)',
  xg_against_opp: 'Opponent defence xG against (ewma10)',
};

// Format feature name for display: underscores to spaces, title-case with an
// abbreviation lookup so model feature names read naturally.
const formatFeatureName = (name) =>
  String(name ?? '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map((word) => {
      const known = FEATURE_ABBREVIATIONS[word.toLowerCase()];
      if (known) return known;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const fmt = (v, dp = 2) => (isNum(v) ? v.toFixed(dp) : '-');
const fmtSigned = (v, dp = 2) => (isNum(v) ? `${v > 0 ? '+' : ''}${v.toFixed(dp)}` : '-');
const pct = (v) => (isNum(v) ? `${Math.round(v * 100)}%` : '-');

const getSignTextClass = (value) => {
  if (value > 0) return 'text-success';
  if (value < 0) return 'text-destructive';
  return 'text-muted-foreground';
};

const getSignRowClass = (value) => {
  if (value > 0) return 'bg-success/10';
  if (value < 0) return 'bg-destructive/10';
  return 'bg-white';
};

const headerCellClass =
  'sticky top-0 z-sticky-header whitespace-nowrap border-b border-borderGrey bg-white p-2 font-semibold';

/** Placeholder rows so the modal layout is stable while data loads. */
function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-4 w-56" />
      <div className="mt-2 flex gap-6">
        <Skeleton className="h-12 w-24" />
        <Skeleton className="h-12 w-24" />
        <Skeleton className="h-12 w-24" />
      </div>
      <div className="mt-2 flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

function StatTile({ label, value, className }) {
  return (
    <div className="min-w-[110px] rounded-token-medium border border-borderGrey p-3">
      <p className="text-token-base text-muted-foreground">{label}</p>
      <p className={cn('text-token-2xl font-medium', className)}>{value}</p>
    </div>
  );
}

/** Signed horizontal bar: negative extends left of centre, positive right. */
function SignedBar({ value, maxAbs }) {
  const scale = maxAbs > 0 ? Math.min(Math.abs(value) / maxAbs, 1) : 0;
  const half = `${(scale * 50).toFixed(1)}%`;
  return (
    <div className="relative h-3 w-full rounded-token-sm bg-neutral-100">
      <div className="absolute inset-y-0 left-1/2 w-px bg-neutral-300" />
      <div
        className={cn(
          'absolute inset-y-0 rounded-token-sm',
          value >= 0 ? 'left-1/2 bg-success/70' : 'right-1/2 bg-destructive/70'
        )}
        style={{ width: half }}
      />
    </div>
  );
}

/**
 * Input value behind a component/term. Team rows (lead contract 2026-09-03,
 * team_model/serve.py) carry features_json = {raw: {term: value}, std: {term: z},
 * elo_own, elo_opp}; optional coef_raw{} / imputed[] are shown when present.
 * Tolerates the flatter {term: value}, {term_raw, term_std} and {raw, std}
 * per-key shapes too.
 */
function featureCell(features, key) {
  const v = features?.raw?.[key] ?? features?.[key];
  const stdNested = features?.std?.[key];
  const coef = features?.coef_raw?.[key];
  const imputed = Array.isArray(features?.imputed) && features.imputed.includes(key);
  const suffix = `${isNum(coef) ? ` · coef ${fmtSigned(coef, 3)}` : ''}${imputed ? ' · imputed' : ''}`;
  if (isNum(v)) {
    return isNum(stdNested) ? `${fmt(v, 3)} (z ${fmt(stdNested, 2)})${suffix}` : `${fmt(v, 3)}${suffix}`;
  }
  if (v && typeof v === 'object') {
    const raw = v.raw ?? v.value;
    const std = v.std ?? v.standardised ?? v.standardized;
    if (isNum(raw) || isNum(std)) return `${fmt(raw, 3)} (z ${fmt(std, 2)})${suffix}`;
  }
  const std = features?.[`${key}_std`] ?? stdNested;
  const raw = features?.[`${key}_raw`];
  if (isNum(raw) || isNum(std)) return `${fmt(raw, 3)} (z ${fmt(std, 2)})${suffix}`;
  return null;
}

const fmtN = (v, dp = 2) => (isNum(v) ? v.toFixed(dp) : null);
const pctN = (v) => (isNum(v) ? `${Math.round(v * 100)}%` : null);
const joinParts = (parts) => parts.filter(Boolean).join(' · ') || null;

/**
 * "Expected" column text for a player component: the per-start expected
 * count and the probability behind the points (owner request, Sep 2026).
 * counts = explanation.e_start_counts, probs = explanation.e_start_probs
 * (both optional; see services/api/services/explanation_service.py).
 */
function expectedText(key, counts, probs, explanation) {
  const c = counts ?? {};
  const p = probs ?? {};
  switch (key) {
    case 'appearance':
      return joinParts([
        pctN(p.p_60 ?? c.p60_given_start) && `${pctN(p.p_60 ?? c.p60_given_start)} to play 60+`,
      ]);
    case 'goals': {
      // counts.goals is TOTAL expected goals (non-pen + pen); the goals points
      // row prices only the non-pen part — penalties are priced on their own
      // row — so show the split when a pen share exists.
      const np = isNum(c.goals) && isNum(c.pen_goal) && c.pen_goal > 0 ? c.goals - c.pen_goal : null;
      return joinParts([
        fmtN(c.goals) &&
          `${fmtN(c.goals)} goals${np != null ? ` (${fmtN(np)} open play + ${fmtN(c.pen_goal)} pen)` : ''}`,
        pctN(p.p_goal) && `${pctN(p.p_goal)} to score`,
        pctN(p.p_2plus_goals) && `${pctN(p.p_2plus_goals)} for 2+`,
      ]);
    }
    case 'assists':
      return joinParts([
        fmtN(c.assists) && `${fmtN(c.assists)} assists`,
        pctN(p.p_assist) && `${pctN(p.p_assist)} to assist`,
      ]);
    case 'clean_sheet':
      return joinParts([pctN(p.p_cs ?? c.cs_prob) && `CS ${pctN(p.p_cs ?? c.cs_prob)}`]);
    case 'conceded':
      return joinParts([fmtN(c.conceded) && `${fmtN(c.conceded)} conceded`]);
    case 'defcon':
      return joinParts([pctN(p.p_defcon ?? c.defcon_prob) && `DefCon ${pctN(p.p_defcon ?? c.defcon_prob)}`]);
    case 'saves':
      return joinParts([fmtN(c.saves) && `${fmtN(c.saves)} saves`]);
    case 'bonus':
      return joinParts([fmtN(c.bonus) && `${fmtN(c.bonus)} bonus`]);
    case 'cards':
      return joinParts([fmtN(c.yc) && `YC ${fmtN(c.yc)}`, fmtN(c.rc, 3) && `RC ${fmtN(c.rc, 3)}`]);
    case 'own_goals':
      return joinParts([fmtN(c.og, 3) && `${fmtN(c.og, 3)} OG`]);
    case 'penalties':
      return joinParts([fmtN(c.pen_goal) && `${fmtN(c.pen_goal)} pen goals (+ misses / saves)`]);
    case 'cameo': {
      const ps = explanation?.features?.prob_starting;
      const pp = explanation?.p_play ?? explanation?.features?.prob_playing;
      return isNum(ps) && isNum(pp) ? `${pctN(Math.max(pp - ps, 0))} sub appearance` : null;
    }
    default:
      return null;
  }
}

/** v2 component breakdown (player points or team log-λ terms). */
function ComponentsView({ explanation, modelType }) {
  const components = explanation?.components ?? explanation?.shap_values ?? {};
  const features = explanation?.features ?? {};
  const isLog = explanation?.link === 'log';
  const isPlayer = modelType === 'player';
  const perStart = isPlayer ? features?.e_start_components : null;
  const counts = isPlayer ? (explanation?.e_start_counts ?? features?.e_start_counts) : null;
  const probs = isPlayer ? (explanation?.e_start_probs ?? features?.e_start_probs) : null;
  const pPlay = explanation?.p_play ?? features?.prob_playing;
  const labels = isPlayer ? PLAYER_COMPONENT_LABELS : TEAM_TERM_LABELS;

  const rows = Object.entries(components)
    .filter(([, v]) => isNum(v))
    .map(([key, value]) => ({
      key,
      label: labels[key] ?? formatFeatureName(key),
      value,
      perStart: isNum(perStart?.[key]) ? perStart[key] : null,
      expected: isPlayer ? expectedText(key, counts, probs, explanation) : null,
      feature: featureCell(features, key),
    }));
  const maxAbs = rows.reduce((m, r) => Math.max(m, Math.abs(r.value)), 0);
  const sum = rows.reduce((s, r) => s + r.value, 0);
  const reconstructed = isNum(explanation?.reconstructed_prediction)
    ? explanation.reconstructed_prediction
    : isLog
      ? Math.exp((explanation?.baseline ?? 0) + sum)
      : (explanation?.baseline ?? 0) + sum;
  const showFeatureCol = rows.some((r) => r.feature != null);
  const showPerStart = rows.some((r) => r.perStart != null);
  const showExpected = rows.some((r) => r.expected != null);
  const additive =
    explanation?.additive ??
    (isNum(explanation?.prediction) && Math.abs(reconstructed - explanation.prediction) <= 0.01);

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-3 border-t border-borderGrey pt-4">
        <StatTile
          label={isPlayer ? 'xPts' : 'Expected goals'}
          value={fmt(explanation?.prediction)}
          className="text-brand-purple1 font-semibold"
        />
        {isLog ? (
          <>
            <StatTile label="Intercept (log λ)" value={fmtSigned(explanation?.baseline, 3)} />
            <StatTile label="Σ terms (log λ)" value={fmtSigned(sum, 3)} />
            <StatTile
              label="exp(intercept + Σ)"
              value={fmt(reconstructed)}
              className={additive ? 'text-success' : 'text-destructive'}
            />
          </>
        ) : (
          <StatTile
            label="Σ components"
            value={fmt(reconstructed)}
            className={additive ? 'text-success' : 'text-destructive'}
          />
        )}
        {isPlayer && (
          <>
            <StatTile label="P(start)" value={pct(features?.prob_starting)} />
            <StatTile label="P(play)" value={pct(pPlay)} />
            <StatTile
              label="P(60+ | start)"
              value={pct(features?.prob_60_start ?? probs?.p_60 ?? counts?.p60_given_start)}
            />
          </>
        )}
      </div>

      {additive === false && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>
            Components do not reconstruct the prediction (
            {fmt(reconstructed)} vs {fmt(explanation?.prediction)}). The row may be from a
            partial write — check the pipeline log.
          </AlertDescription>
        </Alert>
      )}

      <p className="text-token-md font-semibold">
        {isPlayer ? 'Points by component' : 'Log-λ terms'}
      </p>
      <p className="mb-2 text-token-base text-muted-foreground">
        {isPlayer
          ? 'Per start = price × expected count; × P(start) = per start × P(start) (cameo × (P(play) − P(start))); the × P(start) column sums to xPts.'
          : 'Each row is coefficient × standardised input; expected goals = exp(intercept + Σ terms).'}
      </p>

      <div className="max-h-[400px] overflow-auto rounded-token-medium border border-borderGrey bg-white">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className={cn(headerCellClass, 'text-left')}>
                {isPlayer ? 'Component' : 'Term'}
              </th>
              <th className={cn(headerCellClass, 'w-[30%]')} aria-label="contribution bar" />
              {showExpected && (
                <th className={cn(headerCellClass, 'text-left')}>Expected</th>
              )}
              {showPerStart && (
                <th className={cn(headerCellClass, 'text-right')}>Per start</th>
              )}
              {showFeatureCol && (
                <th className={cn(headerCellClass, 'text-right')}>Input</th>
              )}
              <th className={cn(headerCellClass, 'text-right')}>
                {isPlayer ? '× P(start)' : 'log λ'}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={getSignRowClass(r.value)}>
                <td className="border-b border-borderGrey p-2">{r.label}</td>
                <td className="border-b border-borderGrey p-2">
                  <SignedBar value={r.value} maxAbs={maxAbs} />
                </td>
                {showExpected && (
                  <td className="whitespace-nowrap border-b border-borderGrey p-2 text-muted-foreground">
                    {r.expected ?? '-'}
                  </td>
                )}
                {showPerStart && (
                  <td className="border-b border-borderGrey p-2 text-right font-mono text-muted-foreground">
                    {fmtSigned(r.perStart)}
                  </td>
                )}
                {showFeatureCol && (
                  <td className="border-b border-borderGrey p-2 text-right font-mono text-muted-foreground">
                    {r.feature ?? '-'}
                  </td>
                )}
                <td
                  className={cn(
                    'border-b border-borderGrey p-2 text-right font-mono font-semibold',
                    getSignTextClass(r.value)
                  )}
                >
                  {fmtSigned(r.value, isLog ? 3 : 2)}
                </td>
              </tr>
            ))}
            <tr className="bg-neutral-50">
              <td className="p-2 font-semibold">{isLog ? 'Σ terms' : 'Total'}</td>
              <td className="p-2" />
              {showExpected && <td className="p-2" />}
              {showPerStart && (
                <td className="p-2 text-right font-mono text-muted-foreground">
                  {fmtSigned(rows.reduce((s, r) => s + (r.perStart ?? 0), 0))}
                </td>
              )}
              {showFeatureCol && <td className="p-2" />}
              <td className="p-2 text-right font-mono font-semibold">
                {fmtSigned(sum, isLog ? 3 : 2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {isPlayer && <PlayerContext features={features} />}
    </>
  );
}

/** Fixture context + per-start rates behind a v2 player row. */
function PlayerContext({ features }) {
  const rates = features?.rates;
  const hasRates = rates && typeof rates === 'object' && Object.keys(rates).length > 0;
  const priorOnly = features?.prior_only === 1 || features?.prior_only === true;
  const home = features?.home;
  const items = [
    ['λ own', `${fmt(features?.lambda_own)} (exp ${fmt(features?.lambda_own_exp)})`],
    ['λ opp', `${fmt(features?.lambda_opp)} (exp ${fmt(features?.lambda_opp_exp)})`],
    ['Venue', home === 1 || home === true ? 'Home' : home === 0 || home === false ? 'Away' : '-'],
    ['Starts in history', isNum(features?.n_starts) ? String(features.n_starts) : '-'],
  ];

  return (
    <div className="mt-4">
      <div className="mb-2 flex items-center gap-2">
        <p className="text-token-md font-semibold">Fixture context</p>
        {priorOnly && <Badge variant="secondary">position prior only</Badge>}
      </div>
      <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
        {items.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-2 border-b border-borderGrey py-1">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-mono">{v}</span>
          </div>
        ))}
      </div>
      {hasRates && (
        <>
          <p className="mb-1 text-token-base text-muted-foreground">
            Per-start rates (ewm, shrunk to the position prior)
          </p>
          <div className="grid grid-cols-3 gap-x-6 gap-y-1 text-sm sm:grid-cols-5">
            {Object.entries(rates).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2 border-b border-borderGrey py-1">
                <span className="text-muted-foreground">{formatFeatureName(k)}</span>
                <span className="font-mono">{isNum(v) ? v.toFixed(3) : (v ?? '-')}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Legacy SHAP table (rows written by the tree ensembles / PoissonGLM). */
function ShapView({ explanation }) {
  const sortedFeatures = React.useMemo(() => {
    if (!explanation?.shap_values || !explanation?.features) return [];
    return Object.entries(explanation.shap_values)
      .filter(([, v]) => isNum(v))
      .map(([feature, shapValue]) => ({
        feature,
        shapValue,
        featureValue: explanation.features?.[feature],
      }))
      .sort((a, b) => Math.abs(b.shapValue) - Math.abs(a.shapValue));
  }, [explanation]);

  const difference =
    isNum(explanation?.prediction) && isNum(explanation?.baseline)
      ? explanation.prediction - explanation.baseline
      : null;

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-3 border-t border-borderGrey pt-4">
        <StatTile
          label="Prediction"
          value={fmt(explanation?.prediction)}
          className="text-brand-purple1 font-semibold"
        />
        <StatTile
          label="Baseline (Avg)"
          value={fmt(explanation?.baseline)}
          className="text-muted-foreground"
        />
        <StatTile
          label="Difference"
          value={fmtSigned(difference)}
          className={difference != null ? getSignTextClass(difference) : 'text-muted-foreground'}
        />
      </div>

      <p className="text-token-md font-semibold">Feature Contributions (sorted by impact)</p>
      <p className="mb-2 text-token-base text-muted-foreground">
        SHAP values of the conditional model. Positive values push the prediction up, negative
        values push it down.
      </p>

      <div className="max-h-[400px] overflow-auto rounded-token-medium border border-borderGrey bg-white">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className={cn(headerCellClass, 'text-left')}>Feature</th>
              <th className={cn(headerCellClass, 'text-right')}>Value</th>
              <th className={cn(headerCellClass, 'text-right')}>SHAP</th>
            </tr>
          </thead>
          <tbody>
            {sortedFeatures.map(({ feature, shapValue, featureValue }) => (
              <tr key={feature} className={getSignRowClass(shapValue)}>
                <td className="border-b border-borderGrey p-2">{formatFeatureName(feature)}</td>
                <td className="border-b border-borderGrey p-2 text-right font-mono">
                  {isNum(featureValue) ? featureValue.toFixed(3) : (featureValue ?? '-')}
                </td>
                <td
                  className={cn(
                    'border-b border-borderGrey p-2 text-right font-mono font-semibold',
                    getSignTextClass(shapValue)
                  )}
                >
                  {fmtSigned(shapValue, 4)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function ExplanationModal({ open, onClose, modelType, entityId, fixtureId, contextInfo }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [explanation, setExplanation] = useState(null);

  const fetchExplanation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPredictionExplanation(modelType, entityId, fixtureId);
      setExplanation(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch explanation');
    } finally {
      setLoading(false);
    }
  }, [modelType, entityId, fixtureId]);

  useEffect(() => {
    if (open && entityId && fixtureId) {
      fetchExplanation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityId, fixtureId]);

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      setExplanation(null);
      setError(null);
      onClose();
    }
  };

  // Older API builds have no explanation_kind — those rows are SHAP rows.
  const isComponents = explanation?.explanation_kind === 'components';
  const entityName =
    (explanation?.entity_name && explanation.entity_name !== entityId
      ? explanation.entity_name
      : null) ||
    contextInfo?.entityName ||
    explanation?.entity_name ||
    'Unknown';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl gap-0 p-0">
        <DialogHeader className="border-b border-borderGrey px-6 py-4">
          <div className="flex items-center justify-between gap-2 pr-8">
            <DialogTitle className="text-token-xl font-semibold">
              Prediction Explanation
            </DialogTitle>
            <div className="flex items-center gap-2">
              {explanation?.model_name && (
                <Badge variant="outline" className="font-mono text-xs">
                  {explanation.model_name}
                </Badge>
              )}
              <Badge variant={modelType === 'team' ? 'info' : 'secondary'}>
                {modelType === 'team' ? 'Team Model' : 'Player Model'}
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
          {loading && <LoadingSkeleton />}

          {error && (
            <Alert variant="destructive" className="mb-2">
              <AlertDescription className="flex flex-col items-start gap-3">
                <span>{error}</span>
                <Button variant="outline" size="sm" onClick={fetchExplanation}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {!loading && !error && explanation && (
            <div>
              {/* Context Info */}
              <div className="mb-4">
                <p className="text-token-md font-semibold">{entityName}</p>
                <p className="text-token-base text-muted-foreground">
                  {explanation.home_away === 'H' ? 'Home' : explanation.home_away === 'A' ? 'Away' : ''}{' '}
                  vs {explanation.opponent || contextInfo?.opponent || 'Unknown'}
                </p>
              </div>

              {explanation.has_explanation ? (
                isComponents ? (
                  <ComponentsView explanation={explanation} modelType={modelType} />
                ) : (
                  <ShapView explanation={explanation} />
                )
              ) : (
                <Alert variant="info">
                  <AlertDescription>
                    No explanation is stored for this prediction. This may be because:
                    <ul className="mt-1 list-disc pl-5">
                      <li>The row predates explanations (features_json / shap_json empty)</li>
                      <li>The model didn't generate an explanation for this fixture</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-borderGrey px-6 py-3">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
